import { type FormEvent, type ReactElement, useEffect, useState } from "react";
import { HTTPError } from "ky";

import {
  ASSIGNABLE_ROLES,
  type AdminUser,
  type AuditListParams,
  type AuditLogEntry,
  createAdminUser,
  deactivateAdminUser,
  listAdminUsers,
  listAuditLog,
  updateAdminUser,
} from "@/api/admin";
import { Btn2 } from "@/components/hifi/Btn2";
import { Card } from "@/components/hifi/Card";
import { Pill } from "@/components/hifi/Pill";
import { Avatar } from "@/components/hifi/misc";
import { ShellLayout } from "@/components/shell/ShellLayout";
import { useAuthStore } from "@/stores/auth";

type Tab = "users" | "audit";

function roleVariant(
  role: string,
): "accent" | "bad" | "ok" | "warn" | "default" {
  if (role === "OWNER") return "bad";
  if (role === "ADMIN") return "accent";
  if (role === "APPROVER") return "ok";
  if (role === "REVIEWER") return "accent";
  if (role === "INCIDENT_RESPONDER") return "warn";
  return "default";
}

function actionVariant(
  action: string,
): "ok" | "accent" | "bad" | "warn" | "default" {
  if (action === "create") return "ok";
  if (action === "update") return "accent";
  if (action === "delete") return "bad";
  if (action === "approve") return "ok";
  if (action === "reject") return "bad";
  if (action === "deactivate") return "warn";
  return "default";
}

function formatErr(err: unknown, fallback: string): string {
  if (err instanceof HTTPError)
    return `${fallback} (${String(err.response.status)})`;
  return fallback;
}

function UsersTab(): ReactElement {
  const me = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "ASSESSOR",
    password: "",
  });

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      setUsers(await listAdminUsers());
      setError(null);
    } catch (err) {
      setError(formatErr(err, "Load failed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    try {
      await createAdminUser(form);
      setShowForm(false);
      setForm({
        email: "",
        first_name: "",
        last_name: "",
        role: "ASSESSOR",
        password: "",
      });
      await refresh();
    } catch (err) {
      setError(formatErr(err, "Create failed"));
    }
  }

  async function handleRoleChange(id: string, role: string): Promise<void> {
    try {
      await updateAdminUser(id, { role });
      await refresh();
    } catch (err) {
      setError(formatErr(err, "Update failed"));
    }
  }

  async function handleToggleActive(user: AdminUser): Promise<void> {
    try {
      if (user.is_active) {
        if (!window.confirm(`Deactivate ${user.email}?`)) return;
        await deactivateAdminUser(user.id);
      } else {
        await updateAdminUser(user.id, { is_active: true });
      }
      await refresh();
    } catch (err) {
      setError(formatErr(err, "Toggle failed"));
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[14px] font-semibold text-n-900">Users</h2>
          <p className="text-[11px] text-n-500">
            Manage team membership and roles.
          </p>
        </div>
        <Btn2
          variant="accent"
          size="sm"
          icon={showForm ? "x" : "plus"}
          onClick={() => {
            setShowForm((v) => !v);
          }}
        >
          {showForm ? "Cancel" : "Invite user"}
        </Btn2>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-2 border border-bad-bg bg-bad-bg/60 px-3 py-2 text-[12px] text-bad"
        >
          {error}
        </div>
      )}

      {showForm && (
        <Card>
          <div className="border-b border-n-150 px-4 py-2.5">
            <div className="text-[13px] font-semibold text-n-900">
              Invite user
            </div>
          </div>
          <form
            onSubmit={(e) => {
              void handleCreate(e);
            }}
            className="grid grid-cols-1 gap-3 p-4 md:grid-cols-6"
          >
            <input
              required
              type="email"
              placeholder="email"
              value={form.email}
              onChange={(e) => {
                setForm({ ...form, email: e.target.value });
              }}
              className="col-span-2 rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            />
            <input
              required
              placeholder="first name"
              value={form.first_name}
              onChange={(e) => {
                setForm({ ...form, first_name: e.target.value });
              }}
              className="rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            />
            <input
              required
              placeholder="last name"
              value={form.last_name}
              onChange={(e) => {
                setForm({ ...form, last_name: e.target.value });
              }}
              className="rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            />
            <select
              value={form.role}
              onChange={(e) => {
                setForm({ ...form, role: e.target.value });
              }}
              className="rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            >
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <input
              required
              type="password"
              placeholder="initial password (≥12 chars)"
              minLength={12}
              value={form.password}
              onChange={(e) => {
                setForm({ ...form, password: e.target.value });
              }}
              className="rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            />
            <div className="md:col-span-6">
              <Btn2 variant="accent" type="submit">
                Create user
              </Btn2>
            </div>
          </form>
        </Card>
      )}

      <Card>
        {loading ? (
          <p className="p-4 text-[13px] text-n-500">Loading…</p>
        ) : users.length === 0 ? (
          <p className="p-4 text-[13px] text-n-500">No users.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-n-150 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.3px] text-n-500">
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2">Last login</th>
                <th className="px-4 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                const isOwner = u.role === "OWNER";
                const initials = `${u.first_name?.[0] ?? ""}${
                  u.last_name?.[0] ?? ""
                }`.toUpperCase() ||
                  u.email.slice(0, 2).toUpperCase();
                return (
                  <tr
                    key={u.id}
                    className={`border-b border-n-100 last:border-0 ${
                      u.is_active ? "hover:bg-n-50" : "opacity-60"
                    }`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar
                          initials={initials}
                          size={28}
                          color="#fff"
                          bg="var(--n-800)"
                        />
                        <div>
                          <div className="text-[13px] font-medium text-n-900">
                            {u.first_name} {u.last_name}
                          </div>
                          <div className="text-[11px] text-n-500">
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Pill variant={roleVariant(u.role)}>{u.role}</Pill>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-n-700">
                      {u.is_active ? "yes" : "no"}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-n-600">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        {!isOwner && !isSelf && (
                          <select
                            value={u.role}
                            onChange={(e) => {
                              void handleRoleChange(u.id, e.target.value);
                            }}
                            className="rounded-2 border border-n-200 bg-white px-2 py-1 text-[11px] text-n-800 shadow-1 focus:border-a-500 focus:outline-none"
                          >
                            {ASSIGNABLE_ROLES.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        )}
                        {!isOwner && !isSelf && (
                          <Btn2
                            size="xs"
                            variant={u.is_active ? "danger" : "default"}
                            onClick={() => {
                              void handleToggleActive(u);
                            }}
                          >
                            {u.is_active ? "Deactivate" : "Reactivate"}
                          </Btn2>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function AuditTab(): ReactElement {
  const [rows, setRows] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entityFilter, setEntityFilter] = useState<string>("");
  const [actionFilter, setActionFilter] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    const params: AuditListParams = { limit: 200 };
    if (entityFilter) params.entity_type = entityFilter;
    if (actionFilter) params.action = actionFilter;
    listAuditLog(params)
      .then((data) => {
        setRows(data);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(formatErr(err, "Load failed"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [entityFilter, actionFilter]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-[11px] font-medium text-n-600">
          <span className="font-mono uppercase tracking-[0.3px]">Entity</span>
          <select
            value={entityFilter}
            onChange={(e) => {
              setEntityFilter(e.target.value);
            }}
            className="rounded-2 border border-n-200 bg-white px-3 py-1.5 text-[12px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
          >
            <option value="">All</option>
            <option value="asset">asset</option>
            <option value="assessment">assessment</option>
            <option value="threat">threat</option>
            <option value="incident">incident</option>
            <option value="action_plan">action_plan</option>
            <option value="user">user</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-[11px] font-medium text-n-600">
          <span className="font-mono uppercase tracking-[0.3px]">Action</span>
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
            }}
            className="rounded-2 border border-n-200 bg-white px-3 py-1.5 text-[12px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
          >
            <option value="">All</option>
            <option value="create">create</option>
            <option value="update">update</option>
            <option value="delete">delete</option>
            <option value="approve">approve</option>
            <option value="reject">reject</option>
            <option value="advance">advance</option>
            <option value="submit">submit</option>
            <option value="deactivate">deactivate</option>
          </select>
        </label>
        {error && (
          <span role="alert" className="text-[11px] text-bad">
            {error}
          </span>
        )}
      </div>

      <Card>
        {loading ? (
          <p className="p-4 text-[13px] text-n-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-[13px] text-n-500">No audit entries.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-n-150 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.3px] text-n-500">
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Entity</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">Description</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-n-100 align-top last:border-0 hover:bg-n-50"
                >
                  <td className="px-4 py-2.5 whitespace-nowrap font-mono text-[11px] text-n-600">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-n-800">
                    {r.user_name ?? r.user_id ?? "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="font-mono text-[11px] text-n-800">
                      {r.entity_type}
                    </span>
                    <span className="ml-1 font-mono text-[10px] text-n-400">
                      {r.entity_id.slice(0, 8)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <Pill variant={actionVariant(r.action)}>{r.action}</Pill>
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-n-600">
                    {r.description ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

interface AdminPageProps {
  initialTab?: Tab;
}

export function AdminPage({ initialTab = "users" }: AdminPageProps = {}): ReactElement {
  const [tab, setTab] = useState<Tab>(initialTab);

  return (
    <ShellLayout
      active={tab === "users" ? "admin" : "audit"}
      topbar={{
        crumbs: ["Workspace", "Admin"],
        title: "Admin",
        subtitle: "Team & audit",
        tabs: [
          { key: "users", label: "Users" },
          { key: "audit", label: "Audit log" },
        ],
        activeTab: tab,
        onTabChange: (k) => {
          setTab(k as Tab);
        },
      }}
    >
      <div className="p-5">{tab === "users" ? <UsersTab /> : <AuditTab />}</div>
    </ShellLayout>
  );
}

export function AuditLogPage(): ReactElement {
  return <AdminPage initialTab="audit" />;
}
